import React, { useEffect, useState, useRef } from 'react';
import { getStickyContainerStatus } from './helpers';
import { StyledStickyContainer } from './styled';
import { isServerSideRendering } from '../../helpers/env';
import cx from 'classix';

type StickyContainerCommonProps = {
  id: string;
  tempVisibility?: 'show' | 'hide'; // Temporally sets visibility until next scroll
  className?: string;
  children?: React.ReactNode;
  onVisible?: () => void;
};

type StickyContainerBottomProps = {
  position?: 'bottom';
  showAfterScrolling?: number;
};

type StickyContainerTopProps = {
  position?: 'top';
  showAfterScrolling?: never;
};

export type StickyContainerProps = StickyContainerCommonProps & (StickyContainerBottomProps | StickyContainerTopProps);

const StickyContainer = ({
  id,
  position = 'top',
  showAfterScrolling = 0,
  tempVisibility,
  className,
  children,
  onVisible
}: StickyContainerProps): JSX.Element => {
  const positionRef = useRef(0);
  const [show, setShow] = useState(position === 'top');
  const [onVisibleFired, setOnVisibleFired] = useState(false);
  const shouldFireOnVisible = onVisible && !onVisibleFired && show;

  useEffect(() => {
    const handleScroll = () => {
      const { shouldShow, newRefPosition } = getStickyContainerStatus(
        show,
        window.scrollY,
        showAfterScrolling,
        positionRef.current,
        position
      );

      if (shouldShow !== show) {
        setShow(shouldShow);
      }

      if (newRefPosition !== positionRef.current) {
        positionRef.current = newRefPosition;
      }

      if (shouldFireOnVisible) {
        setOnVisibleFired(true);
        onVisible();
      }
    };

    if (isServerSideRendering()) {
      return;
    }
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [position, showAfterScrolling, show, onVisibleFired, onVisible, shouldFireOnVisible]);

  useEffect(() => {
    tempVisibility && setShow(tempVisibility === 'show' ? true : false);
  }, [tempVisibility]);

  const classNames = cx(className, position, show ? 'show' : 'hide');

  return (
    <StyledStickyContainer id={id} className={classNames}>
      {children}
    </StyledStickyContainer>
  );
};

export default StickyContainer;
